import React from 'react';
import {
  MathContent, PaperImage, displayStyleLoneFormula, optionHasText, prepareSittingHtml,
} from '../../../../quiz-generation/components/examRender';
import { outcomeLabel, plural, questionKindLabel } from './lib/activityFormat';
import { questionTime } from './lib/paperReview';

// One question of a paper review, drawn with the paper player's renderer
// (examRender): question, option, statement, passage and explanation HTML go
// through prepareSittingHtml (delimiter repair, then the one sanitiser) into
// MathContent, which PaperReview typesets; an option that is one formula is
// then set in display style (displayStyleLoneFormula), as the player sets it.
// What the student wrote is their own words and is only ever text.

const Html = ({ html, className, option = false }) => {
  const prepared = prepareSittingHtml(html || '');
  return <MathContent className={className} html={option ? displayStyleLoneFormula(prepared) : prepared} />;
};

/** An LNAT passage or exhibit, drawn once above the questions that share it; or a custom quiz question's own passage, in its card. */
export const PassageBlock = ({ exhibit }) => (
  <div className="td-pr-passage">
    <span className="td-eyebrow">Passage</span>
    <Html
      html={exhibit.text}
      className={exhibit.is_html
        ? 'td-pr-html td-pr-passage__body'
        : 'td-pr-html td-pr-html--text td-pr-passage__body'}
    />
  </div>
);

const Tag = ({ children }) => <span className="td-pr-tag">{children}</span>;

const OptionRow = ({ option, chosen }) => {
  const tone = option.is_correct ? ' td-pr-opt--correct' : (chosen ? ' td-pr-opt--wrong' : '');
  return (
    <li className={`td-pr-opt${tone}`}>
      <span className="td-pr-opt__letter">{option.letter}</span>
      <span className="td-pr-opt__body">
        {optionHasText(option.text) ? <Html html={option.text} option /> : null}
        <PaperImage url={option.image_url} alt={`Option ${option.letter}`} maxHeight="260px" />
      </span>
      <span className="td-pr-opt__tags">
        {chosen ? <Tag>Student&apos;s answer</Tag> : null}
        {option.is_correct ? <Tag>Correct</Tag> : null}
      </span>
    </li>
  );
};

const yesNo = (value) => (value == null ? 'Not answered' : (value ? 'Yes' : 'No'));

const YesNoItems = ({ items }) => (
  <ul className="td-pr-opts">
    {items.map((item, i) => {
      const right = item.answered_yes != null && item.answered_yes === item.correct_yes;
      const tone = item.answered_yes == null ? '' : (right ? ' td-pr-opt--correct' : ' td-pr-opt--wrong');
      return (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i} className={`td-pr-opt td-pr-opt--wide${tone}`}>
          <span className="td-pr-opt__letter">{i + 1}</span>
          <span className="td-pr-opt__body"><Html html={item.text} /></span>
          <span className="td-pr-opt__tags">
            <Tag>{`Student: ${yesNo(item.answered_yes)}`}</Tag>
            <Tag>{`Correct: ${yesNo(item.correct_yes)}`}</Tag>
          </span>
        </li>
      );
    })}
  </ul>
);

const MostLeastOptions = ({ options }) => (
  <ul className="td-pr-opts">
    {options.map((option, i) => {
      const correctPick = (option.chosen === 'most' && option.is_correct_most)
        || (option.chosen === 'least' && option.is_correct_least);
      const tone = option.is_correct_most || option.is_correct_least
        ? ' td-pr-opt--correct'
        : (option.chosen && !correctPick ? ' td-pr-opt--wrong' : '');
      return (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i} className={`td-pr-opt td-pr-opt--wide${tone}`}>
          <span className="td-pr-opt__letter">{i + 1}</span>
          <span className="td-pr-opt__body"><Html html={option.text} /></span>
          <span className="td-pr-opt__tags">
            {option.chosen ? <Tag>{`Student: ${option.chosen === 'most' ? 'Most' : 'Least'}`}</Tag> : null}
            {option.is_correct_most ? <Tag>Correct: Most</Tag> : null}
            {option.is_correct_least ? <Tag>Correct: Least</Tag> : null}
          </span>
        </li>
      );
    })}
  </ul>
);

/** The student's essay, as text, with the statement they chose and a link to its Writing piece. */
const EssayAnswer = ({ essay, answer, onOpenPiece }) => (
  <div className="td-pr-essay">
    {essay?.prompt ? (
      <>
        <span className="td-eyebrow">Statement chosen</span>
        <Html html={essay.prompt} className="td-pr-html" />
      </>
    ) : null}
    {answer?.text ? (
      <>
        <span className="td-eyebrow">{`Student's essay · ${plural(essay?.word_count ?? 0, 'word')}`}</span>
        <p className="td-act-quote">{answer.text}</p>
      </>
    ) : <p className="td-act-meta">Nothing written.</p>}
    {essay?.writing_piece && onOpenPiece ? (
      <button type="button" className="td-linkbtn" onClick={() => onOpenPiece(essay.writing_piece)}>
        Open in Writing
      </button>
    ) : null}
  </div>
);

/** A worked solution, step by step, collapsed under the explanation. */
const Steps = ({ steps }) => (
  <details className="td-pr-expl td-pr-steps">
    <summary>{`Worked solution · ${plural(steps.length, 'step')}`}</summary>
    <ol className="td-pr-steps__list">
      {steps.map((step) => (
        <li key={step.number}><Html html={step.text} className="td-pr-html" /></li>
      ))}
    </ol>
  </details>
);

/**
 * One question card: number, topic, time (only when recorded), outcome, a
 * passage of its own when it has one (a custom quiz question), the
 * question, then by kind: options with "Student's answer" and "Correct", the
 * yes/no or most/least detail, or the essay. `answer_note` is a line of
 * plain text for a question whose options were not saved (an older custom
 * quiz). A collapsible explanation, and under it the worked solution's
 * steps when there are any.
 */
const PaperReviewQuestion = ({ question, onOpenPiece, cardRef }) => {
  const outcome = outcomeLabel(question.outcome);
  const time = questionTime(question);
  const chosenLetter = question.student_answer?.letter ?? null;
  return (
    <article
      className="td-pr-q"
      id={`paper-q-${question.number}`}
      ref={cardRef}
      tabIndex={-1}
      aria-label={`Question ${question.number}`}
    >
      <div className="td-pr-q__head">
        <b>{`QUESTION ${question.number}`}</b>
        <span className="td-pr-q__meta">
          {question.topic ? <span>{question.topic}</span> : null}
          {question.kind !== 'multiple_choice' && questionKindLabel(question.kind)
            ? <span>{questionKindLabel(question.kind)}</span> : null}
          {time ? <span>{time}</span> : null}
          {outcome ? <span className={`td-act-pill td-act-pill--${outcome.tone}`}>{outcome.text}</span> : null}
        </span>
      </div>
      {question.passage ? <PassageBlock exhibit={question.passage} /> : null}
      {question.text ? <Html html={question.text} className="td-pr-html td-pr-q__text" /> : null}
      <PaperImage url={question.image_url} alt={`Question ${question.number}`} />

      {question.kind === 'multiple_choice' && question.options.length ? (
        <ul className="td-pr-opts">
          {question.options.map((option) => (
            <OptionRow key={option.letter} option={option} chosen={chosenLetter === option.letter} />
          ))}
        </ul>
      ) : null}
      {question.kind === 'yes_no_group' && question.yes_no ? <YesNoItems items={question.yes_no} /> : null}
      {question.kind === 'most_least_group' && question.most_least
        ? <MostLeastOptions options={question.most_least} /> : null}
      {question.kind === 'essay_choice_group' ? (
        <EssayAnswer essay={question.essay} answer={question.student_answer} onOpenPiece={onOpenPiece} />
      ) : null}
      {question.kind === 'multiple_choice' && question.student_answer?.text ? (
        <p className="td-act-quote">{question.student_answer.text}</p>
      ) : null}

      {question.answer_note ? <p className="td-act-meta">{question.answer_note}</p> : null}

      {question.explanation ? (
        <details className="td-pr-expl">
          <summary>Explanation</summary>
          <Html html={question.explanation} className="td-pr-html" />
        </details>
      ) : null}
      {question.steps?.length ? <Steps steps={question.steps} /> : null}
    </article>
  );
};

export default PaperReviewQuestion;
